import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import SideMenu from "./SideMenu";
import AppLogo from "../img/AppLogo.svg";
import PurpleArrowRight from "../img/PurpleArrowRight.svg";
import BackgroundImg from "../img/BackgroundNetImg.svg";
import { useNavigate } from "react-router-dom";
import api from "../../api/axiosInstance";
import CustomLoader from "./CustomLoader";
import { motion } from "framer-motion";
import { div } from "motion/react-client";

const MyApplications = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true); // initial loader
  const [loadingMore, setLoadingMore] = useState(false); // bottom loader for next pages
  const [nextUrl, setNextUrl] = useState(null); // API 'next' link
  const [count, setCount] = useState(0); // total available (from API)
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Avoid duplicate fetches (e.g., rapid intersections)
  const isFetchingRef = useRef(false);

  // Normalize an absolute "next" URL into a relative path for axiosInstance
  const toRelative = (url) => url?.replace(/^https?:\/\/[^/]+\//, "") || null;

  const fetchPage = async (
    url = "admin-app/api/v1/list-project",
    { append = false } = {}
  ) => {
    try {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      append ? setLoadingMore(true) : setLoading(true);
      setError(null);

      const res = await api.get(toRelative(url));
      const data = res?.data?.data;

      const results = data?.results ?? [];
      setApps((prev) => (append ? [...prev, ...results] : results));

      // Set page size from first page results length (fallback to previous)
      if (!append && results.length > 0) {
        setPageSize(results.length);
      }

      setCount(data?.count ?? 0);
      setNextUrl(toRelative(data?.next)); // null when no more pages

      if (!append && !initialLoadComplete) {
        setInitialLoadComplete(true);
      }
    } catch (err) {
      console.error("Error fetching apps:", err);
      setError("Failed to load products.");
      setInitialLoadComplete(true);
    } finally {
      isFetchingRef.current = false;
      append ? setLoadingMore(false) : setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rootEl = scrollContainerRef.current;
    const sentinelEl = sentinelRef.current;
    if (!rootEl || !sentinelEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && nextUrl && !loading && !loadingMore) {
          // Load the next page
          fetchPage(nextUrl, { append: true });
        }
      },
      {
        root: rootEl, // VERY IMPORTANT: observe inside your scrollable container
        rootMargin: "200px", // start loading a bit early
        threshold: 0.1,
      }
    );

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [nextUrl, loading, loadingMore]);

  const AppDeta = (app) => {
    navigate("/productdetails", {
      state: { id: app.id, project_type: app.project_type },
    });
  };

  if (loading && !initialLoadComplete) {
    return <CustomLoader></CustomLoader>;
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <div className="">
      <Header></Header>
      <div
        className="flex flex-col md:flex-row px-5 sm:px-10 w-full bg-white bg-cover bg-center min-h-[calc(100vh-64px)]"
        style={{
          backgroundImage: `url(${BackgroundImg})`,
          backgroundPosition: "50% 0%",
        }}
      >
        <div className="flex flex-col lg:flex-row w-full">
          {/* <div className="w-full lg:w-2/12 ">
            <SideMenu />
          </div>
          <div className="hidden md:block w-px bg-gray-300 h-[calc(100vh-64px)] " /> */}

          {/* Scroll container needs a ref */}
          <div
            ref={scrollContainerRef}
            className="w-full  py-6 overflow-y-auto max-h-[calc(100vh-64px)] scrollbar-hide"
          >
            <div className="flex justify-between items-center">
              <h1 className="font-inter text-2xl font-semibold">
                My Applications
              </h1>
              <button
                className="bg-primary px-3 mr-1 h-8 rounded-md text-white text-sm font-semibold flex items-center justify-center hover:scale-105 transition-transform duration-300"
                onClick={() => navigate("/addApplication")}
                style={{ willChange: "transform" }}
              >
                Add New Application
              </button>
            </div>

            {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

            {initialLoadComplete && (
              <>
                {apps.length === 0 ? (
                  <div className="flex items-center justify-center h-[80%]">
                    <span>No applications added yet.</span>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {apps.map((app) => (
                        <motion.div
                          key={app.id}
                          variants={cardVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          layout
                          className="flex gap-2 border p-3 rounded border-[#D9D9D9] bg-white cursor-pointer"
                          style={{ boxShadow: " 0px 4px 4px 0px #00000040" }}
                          onClick={() => AppDeta(app)}
                        >
                          <img
                            src={app.logo || AppLogo}
                            alt={app.name}
                            className="border-1 border-[#00000040] shadow rounded w-16 h-16 object-cover"
                            onError={(e) => (e.currentTarget.src = AppLogo)}
                          />
                          <div className="flex flex-col gap-1 overflow-hidden">
                            <span className="text-base font-medium truncate">
                              {app.name}
                            </span>
                            <span className="text-xs text-gray-600 line-clamp-2">
                              {app.description}
                            </span>
                            <span className="text-[#9367D7] text-sm flex gap-2 items-center">
                              View <img src={PurpleArrowRight} alt="" />
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Bottom loader + sentinel */}
                    <div className="flex items-center justify-center py-4">
                      {loadingMore && (
                        <span className="text-sm text-gray-600">
                          Loading more…
                        </span>
                      )}
                    </div>

                    {/* Invisible sentinel observed by IntersectionObserver */}
                    <div ref={sentinelRef} style={{ height: 1 }} />
                  </>
                )}
              </>
            )}

            {/* Grid of cards */}

            {/* Optional: when no more pages */}
            {/* {!nextUrl && !loadingMore && apps.length < count && (
              <div className="text-center py-2 text-xs text-gray-500">
                All items loaded.
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyApplications;
